/**
 * React Query hooks for Companies and workspace arche approvals (ADR-022).
 * Server state only; every mutation invalidates the queries it changes.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { CompanyMemberRole } from '@/lib/api';

export function useMyCompanies(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.companies.mine(),
    enabled: options?.enabled ?? true,
    queryFn: async () => (await api.listMyCompanies()).companies,
  });
}

export function useCompany(companyId: string | null) {
  return useQuery({
    queryKey: queryKeys.companies.detail(companyId ?? 'none'),
    enabled: companyId !== null,
    queryFn: () => api.getCompany(companyId as string),
  });
}

/** Company approvals come a page at a time (the server caps a page); "Show more" asks for the next. */
export const COMPANY_APPROVALS_PAGE_SIZE = 50;

export function useCompanyApprovals(companyId: string | null) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.companies.approvals(companyId ?? 'none'),
    enabled: companyId !== null,
    queryFn: ({ pageParam }) =>
      api.listCompanyApprovals(companyId as string, {
        limit: COMPANY_APPROVALS_PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.approvals.length, 0);
      // An empty page ends paging even if the total moved underneath us.
      return lastPage.approvals.length > 0 && loaded < lastPage.total ? loaded : undefined;
    },
  });
  return {
    approvals: query.data?.pages.flatMap((p) => p.approvals) ?? [],
    total: query.data?.pages[query.data.pages.length - 1]?.total ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    hasMore: query.hasNextPage,
    isFetchingMore: query.isFetchingNextPage,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    },
  };
}

export function useCompanyApprovalActivity(companyId: string | null, archeId: string | null) {
  return useQuery({
    queryKey: queryKeys.companies.approvalActivity(companyId ?? 'none', archeId ?? 'none'),
    enabled: companyId !== null && archeId !== null,
    queryFn: async () =>
      (await api.getCompanyApprovalActivity(companyId as string, archeId as string)).activity,
  });
}

export function useCompanyBilling(companyId: string | null, month: string) {
  return useQuery({
    queryKey: queryKeys.companies.billing(companyId ?? 'none', month),
    enabled: companyId !== null,
    queryFn: () => api.getCompanyBilling(companyId as string, month),
  });
}

/** One invalidation for anything that changes a Company's settings view. */
function useInvalidateCompany(companyId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: queryKeys.companies.detail(companyId) });
    void qc.invalidateQueries({ queryKey: queryKeys.companies.mine() });
  };
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.createCompany(name),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.companies.mine() }),
  });
}

export function useUpdateCompany(companyId: string) {
  const invalidate = useInvalidateCompany(companyId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; billingUserId?: string; trustFiasArches?: boolean }) =>
      api.updateCompany(companyId, body),
    onSuccess: (_r, body) => {
      invalidate();
      // Trust Fias is inherited by every linked workspace's approvals view.
      if (body.trustFiasArches !== undefined) {
        void qc.invalidateQueries({ queryKey: queryKeys.vault.approvalsAll });
      }
    },
  });
}

/** Sends a seat INVITATION; the person joins when they accept. */
export function useAddCompanyMember(companyId: string) {
  const invalidate = useInvalidateCompany(companyId);
  return useMutation({
    mutationFn: (body: { username: string; role: CompanyMemberRole }) =>
      api.addCompanyMember(companyId, body),
    onSuccess: invalidate,
  });
}

export function useCancelCompanyMemberInvitation(companyId: string) {
  const invalidate = useInvalidateCompany(companyId);
  return useMutation({
    mutationFn: (invitationId: string) =>
      api.cancelCompanyMemberInvitation(companyId, invitationId),
    onSuccess: invalidate,
  });
}

// ── Seat invitations — the invitee's side ───────────────────────────────────

export function useMyCompanySeatInvitations(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.companies.myInvitations(),
    queryFn: async () => (await api.listMyCompanySeatInvitations()).invitations,
    enabled: options?.enabled ?? true,
  });
}

export function useAcceptCompanySeatInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => api.acceptCompanySeatInvitation(invitationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.companies.myInvitations() });
      // The new seat puts the Company in the caller's list.
      void qc.invalidateQueries({ queryKey: queryKeys.companies.mine() });
    },
  });
}

export function useDeclineCompanySeatInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => api.declineCompanySeatInvitation(invitationId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.companies.myInvitations() }),
  });
}

export function useUpdateCompanyMemberRole(companyId: string) {
  const invalidate = useInvalidateCompany(companyId);
  return useMutation({
    mutationFn: (input: { membershipId: string; role: CompanyMemberRole }) =>
      api.updateCompanyMemberRole(companyId, input.membershipId, input.role),
    onSuccess: invalidate,
  });
}

export function useRemoveCompanyMember(companyId: string) {
  const invalidate = useInvalidateCompany(companyId);
  return useMutation({
    mutationFn: (membershipId: string) => api.removeCompanyMember(companyId, membershipId),
    onSuccess: invalidate,
  });
}

export function useProposeCompanyLink(companyId: string) {
  const invalidate = useInvalidateCompany(companyId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (holderId: string) => api.proposeCompanyLink(companyId, holderId),
    onSuccess: (_r, holderId) => {
      invalidate();
      // The proposer is on that workspace's roster: their own Workspace
      // Controls shows the invitation, and the owners' feed changes.
      void qc.invalidateQueries({ queryKey: queryKeys.vault.company(holderId) });
      void qc.invalidateQueries({ queryKey: queryKeys.vault.me.companyLinkProposals() });
    },
  });
}

/**
 * Link a workspace the CALLER OWNS: the two-sided act (Company proposes,
 * workspace owner accepts) collapses into one because the caller is both
 * parties. Any Company role may propose (owner or admin), so offer this to
 * every member who owns the workspace. Never adds owners-as-admins — the
 * caller is already on that roster.
 */
export function useLinkOwnedWorkspace(companyId: string) {
  const invalidate = useInvalidateCompany(companyId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (holderId: string) => {
      const { invitation } = await api.proposeCompanyLink(companyId, holderId);
      try {
        return await api.acceptCompanyLink(invitation.invitationId, { addOwnersAsAdmins: false });
      } catch (err) {
        // Half done: the proposal exists but the link did not happen. Withdraw
        // it so the workspace does not sit "invited" with no Withdraw button
        // and the retry does not hit the one-open-proposal conflict. Best
        // effort — the original failure is what the user sees.
        try {
          await api.cancelCompanyLink(companyId, invitation.invitationId);
        } catch {
          // best effort — the original failure is what the user sees
        }
        throw err;
      }
    },
    // Settled, not success: a failed half leaves server state the screen
    // must re-read either way.
    onSettled: (_r, _e, holderId) => {
      invalidate();
      void qc.invalidateQueries({ queryKey: queryKeys.vault.company(holderId) });
      void qc.invalidateQueries({ queryKey: queryKeys.vault.approvals(holderId) });
    },
  });
}

export function useCancelCompanyLink(companyId: string) {
  const invalidate = useInvalidateCompany(companyId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { invitationId: string; holderId: string }) =>
      api.cancelCompanyLink(companyId, input.invitationId),
    onSuccess: (_r, input) => {
      invalidate();
      void qc.invalidateQueries({ queryKey: queryKeys.vault.company(input.holderId) });
      void qc.invalidateQueries({ queryKey: queryKeys.vault.me.companyLinkProposals() });
    },
  });
}

export function useDeleteCompany(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteCompany(companyId),
    onSuccess: () => {
      // Drop the deleted Company from the cached list at once, so the page
      // does not re-select it for a render while the list refetches, and
      // forget its detail rather than refetching a 404.
      qc.setQueryData<Array<{ company: { companyId: string } }>>(
        queryKeys.companies.mine(),
        (prev) => prev?.filter((c) => c.company.companyId !== companyId),
      );
      qc.removeQueries({ queryKey: queryKeys.companies.detail(companyId) });
      void qc.invalidateQueries({ queryKey: queryKeys.companies.all });
      // Every workspace's Company card and approvals list change with it.
      void qc.invalidateQueries({ queryKey: queryKeys.vault.companyAll });
      void qc.invalidateQueries({ queryKey: queryKeys.vault.approvalsAll });
    },
  });
}

export function useUnlinkCompanyWorkspace(companyId: string) {
  const invalidate = useInvalidateCompany(companyId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (holderId: string) => api.unlinkCompanyWorkspace(companyId, holderId),
    onSuccess: (_r, holderId) => {
      invalidate();
      void qc.invalidateQueries({ queryKey: queryKeys.vault.company(holderId) });
      void qc.invalidateQueries({ queryKey: queryKeys.vault.approvals(holderId) });
    },
  });
}

/** A Company decision is inherited by every linked workspace's approvals view. */
function useInvalidateCompanyApprovals(companyId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: queryKeys.companies.approvals(companyId) });
    void qc.invalidateQueries({ queryKey: queryKeys.vault.approvalsAll });
    // Which arches the email settings offer to send as the Company
    // (`eligibleArches`) follows these decisions.
    void qc.invalidateQueries({ queryKey: queryKeys.companies.email(companyId) });
  };
}

export function useDecideCompanyApproval(companyId: string) {
  const invalidate = useInvalidateCompanyApprovals(companyId);
  return useMutation({
    mutationFn: (body: { archeId: string; decision: 'approve' | 'deny'; note?: string }) =>
      api.decideCompanyApproval(companyId, body),
    onSuccess: invalidate,
  });
}

export function useRevokeCompanyApproval(companyId: string) {
  const invalidate = useInvalidateCompanyApprovals(companyId);
  return useMutation({
    mutationFn: (approvalId: string) => api.revokeCompanyApproval(companyId, approvalId),
    onSuccess: invalidate,
  });
}

// ── The workspace side ──────────────────────────────────────────────────────

/** Open proposals on the workspaces the caller owns — drives the alert dot and the banner. */
export function useMyCompanyLinkProposals(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.vault.me.companyLinkProposals(),
    queryFn: async () => (await api.listMyCompanyLinkProposals()).proposals,
    enabled: options?.enabled ?? true,
  });
}

export function useWorkspaceCompany(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.vault.company(holderId ?? 'none'),
    enabled: holderId !== null,
    queryFn: () => api.getWorkspaceCompany(holderId as string),
  });
}

/** Open proposals' context changes under the owner (owners, payer): re-read on any outcome. */
export function useAcceptCompanyLink(holderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      invitationId: string;
      addOwnersAsAdmins: boolean;
      /** The Company owners the screen named; required with the checkbox. */
      expectedOwnerPrincipalIds?: string[];
    }) =>
      api.acceptCompanyLink(input.invitationId, {
        addOwnersAsAdmins: input.addOwnersAsAdmins,
        expectedOwnerPrincipalIds: input.expectedOwnerPrincipalIds,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.vault.approvals(holderId) });
      void qc.invalidateQueries({ queryKey: queryKeys.vault.team.forHolder(holderId) });
      void qc.invalidateQueries({ queryKey: queryKeys.vault.me.companyLinkNotices() });
    },
    // Settled, not success: a COMPANY_OWNERS_CHANGED refusal must redraw the
    // screen with the owners as they are now.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.vault.company(holderId) });
      void qc.invalidateQueries({ queryKey: queryKeys.vault.me.companyLinkProposals() });
    },
  });
}

export function useDeclineCompanyLink(holderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => api.declineCompanyLink(invitationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.vault.company(holderId) });
      void qc.invalidateQueries({ queryKey: queryKeys.vault.me.companyLinkProposals() });
    },
  });
}

/** Workspaces the caller belongs to that joined a Company recently (the member notice). */
export function useMyCompanyLinkNotices(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.vault.me.companyLinkNotices(),
    queryFn: async () => (await api.listMyCompanyLinkNotices()).notices,
    enabled: options?.enabled ?? true,
  });
}

export function useWorkspaceApprovals(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.vault.approvals(holderId ?? 'none'),
    enabled: holderId !== null,
    queryFn: () => api.listWorkspaceApprovals(holderId as string),
  });
}

export function useDecideWorkspaceApproval(holderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { archeId: string; decision: 'approve' | 'deny'; note?: string }) =>
      api.decideWorkspaceApproval(holderId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.vault.approvals(holderId) }),
  });
}

export function useRevokeWorkspaceApproval(holderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (approvalId: string) => api.revokeWorkspaceApproval(holderId, approvalId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.vault.approvals(holderId) }),
  });
}

export function useWorkspaceApprovalActivity(holderId: string | null, archeId: string | null) {
  return useQuery({
    queryKey: queryKeys.vault.approvalActivity(holderId ?? 'none', archeId ?? 'none'),
    enabled: holderId !== null && archeId !== null,
    queryFn: async () =>
      (await api.getWorkspaceApprovalActivity(holderId as string, archeId as string)).activity,
  });
}

export function useWorkspaceAccessDenials(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.vault.accessDenials(holderId ?? 'none'),
    enabled: holderId !== null,
    queryFn: async () => (await api.listWorkspaceAccessDenials(holderId as string)).denials,
  });
}
